<template>
  <div class="ForumResponseAvatar" :class="{ ForumResponseAvatar_sm: small }">
    <LLPopper
      v-if="isOwnerVisible"
      :offset="[5, 0]"
      placement="right"
      tooltip-white
      tooltip-with-arrow
      :disabled="!canOpenProfile"
    >
      <template #trigger>
        <button class="ForumResponseAvatar__user" type="button" :aria-label="ownerName">
          <ProspectAvatar v-if="prospect" disable-tooltip small :prospect="prospect" />
          <UserAvatar v-else-if="user" small :user="user" />
        </button>
      </template>
      <template #tooltip>
        <MemberProfileCard :chat-id="chatId" :member-id="prospect?.id || user?.id" />
      </template>
    </LLPopper>
    <LLPopper v-else-if="isOwnerHidden" :offset="[5, 0]" placement="right" tooltip-white tooltip-with-arrow>
      <template #trigger>
        <button
          class="ForumResponseAvatar__anonymous"
          type="button"
          :aria-label="$t('forum.response.anonymous_reply_user_name')"
        >
          {{ anonymousReplyLetter }}
        </button>
      </template>
      <template #tooltip>
        <MemberProfileCard :anonymous-id="anonymousId" />
      </template>
    </LLPopper>
    <LLPopper
      v-else-if="isOwnerModerator"
      :offset="[5, 0]"
      placement="right"
      tooltip-white
      tooltip-with-arrow
    >
      <template #trigger>
        <button
          class="ForumResponseAvatar__moderator"
          type="button"
          :aria-label="$t('forum.response.moderator_reply_user_name')"
        >
          <LogoRound class="ForumResponseAvatar__moderator-logo" />
        </button>
      </template>
      <template #tooltip>
        <MemberProfileCard moderator />
      </template>
    </LLPopper>
  </div>
</template>

<script>
import ProspectAvatar from '@/components/modules/prospects/ProspectAvatar.vue'
import UserAvatar from '@/components/modules/users/UserAvatar.vue'
import LogoRound from '@/assets/icons/LogoRound.vue'
import LLPopper from '@/components/utils/LLPopper.vue'
import MemberProfileCard from '@/components/pages/forum/member-profile/MemberProfileCard.vue'
import { ForumResponseModel } from '@/models/forum/forumResponse'

export default {
  name: 'ForumResponseAvatar',
  components: { LLPopper, LogoRound, MemberProfileCard, UserAvatar, ProspectAvatar },
  props: {
    response: { type: ForumResponseModel, required: true },
    small: { type: Boolean, default: false },
    chatId: { type: [Number, String], default: null }
  },
  computed: {
    isOwnerVisible() {
      return !this.isOwnerHidden && !this.isOwnerModerator
    },
    isOwnerHidden() {
      return (
        this.response.anonymousState &&
        this.response.prospect?.id !== this.$store.getters['user/user']?.id &&
        !this.response.isOwnerModerator
      )
    },
    isOwnerModerator() {
      return (
        this.response.anonymousState &&
        this.response.prospect?.id !== this.$store.getters['user/user']?.id &&
        this.response.isOwnerModerator
      )
    },
    anonymousReplyLetter() {
      return this.$t('forum.response.anonymous_reply_user_name').slice(0, 1)
    },
    prospect() {
      return this.response?.prospect
    },
    user() {
      return this.response?.user
    },
    ownerName() {
      return (
        this.prospect?.fullNameWithShortLastName ||
        this.user?.fullNameWithShortLastName ||
        this.$t('not-available')
      )
    },
    canOpenProfile() {
      // Participants and staff users are resolved by the same endpoint, so any known author
      // with a chat opens the card.
      return !!(this.prospect?.id || this.user?.id) && !!this.chatId
    },
    anonymousId() {
      return this.response?.anonymousId
    }
  }
}
</script>

<style scoped lang="scss">
.ForumResponseAvatar {
  &__user {
    @apply w-6 h-6 p-0 flex-shrink-0;
    @screen md {
      @apply w-8 h-8;
    }
  }
  &__anonymous {
    @apply w-6 h-6 p-0 rounded-full object-cover bg-neutral-01-400 text-body-02 text-white flex items-center justify-center;
    @screen md {
      @apply w-8 h-8;
    }
  }
  &__moderator {
    @apply w-6 h-6 p-0 flex-shrink-0 flex items-center justify-center;
    @screen md {
      @apply w-8 h-8;
    }
  }
  &__moderator-logo {
    @apply w-full h-full;
  }

  &.ForumResponseAvatar_sm {
    .ForumResponseAvatar__user,
    .ForumResponseAvatar__anonymous,
    .ForumResponseAvatar__moderator {
      @apply w-5 h-5;
      @screen md {
        @apply w-6 h-6;
      }
    }
    .ForumResponseAvatar__anonymous {
      font-size: 12px;
      line-height: 1;
    }
  }
}
</style>
